import { VoidCallback } from '@deqo/foundation';
import { easeInOutQuint } from 'just-curves';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { numberStep, pointStep, stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { ClipDataBuilder, ClipboardService } from '../../../platform/Clipboard';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { alert } from '../../packages/alerts/AlertCommands';
import { Camera } from '../Camera';
import { CameraStartingPosition } from '../CameraStartingPosition';
import { EditorSession } from '../EditorSession';
import { Selection } from '../Selection';
import { Phys } from '../entities/Phys';
import { Physical, RelativeTo } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';

export const cameraMoveBy = defineCommand(
    'cameraMoveBy',
    'Camera: Move By',
    (cc, [x, y]: [number, number]) => {
        const camera = cc.call(Camera);
        camera.move(new Point(x, y));
    },
    [pointStep('distance', 'Distance:')]
);

export const cameraMoveTo = defineCommand(
    'cameraMoveTo',
    'Camera: Move To',
    (cc, [x, y, smooth]: [number, number, boolean?]) => {
        const camera = cc.call(Camera);
        if (smooth) {
            camera.animateTo(new Point(x, y), camera.scale);
        }
        else {
            camera.moveTo(new Point(x, y));
        }
    },
    [pointStep('position', 'Position:')]
);

export const cameraMoveToFrame = defineCommand(
    'cameraMoveToFrame',
    null,
    (cc, [x, y, w, h]: [number, number, number, number]) => {
        const camera = cc.call(Camera);
        const to = new Point(x, y);
        const dist = Point.vector(camera.position, to);
        camera.animateToFrame(new Rect(x, y, w, h), Math.max(dist.length() / 2, 250), easeInOutQuint);
    },
);

export const cameraMoveToStart = defineCommand(
    'cameraMoveToStart',
    'Camera: Move To Start',
    (cc) => {
        const camera = cc.call(Camera);
        const objects = cc.call(ObjectManager);

        const [csp] = Array.from(objects.getAll(CameraStartingPosition)).sort((a, b) => a.id.localeCompare(b.id));
        if (!!csp) {
            camera.animateToFrame(csp.toRect());
        }
        else {
            cc.call(CommandDispatcher)(cameraMoveToAll);
        }
    }
);

export const cameraMoveToAll = defineCommand(
    'cameraMoveToAll',
    'Camera: Move To All',
    (cc) => {

        const camera = cc.call(Camera);
        const objects = cc.call(ObjectManager);

        const topLayer = objects.getRoot().polarize(Physical);
        if (topLayer.length <= 0) return null;
        const frame = Rect.fromMany(topLayer.map(x => x.bounds()));
        camera.animateToFrame(frame);
    }
);

export const cameraMoveToOrigin = defineCommand(
    'cameraMoveToOrigin',
    'Camera: Move To Origin',
    (cc) => cc.call(Camera).setView([0, 0], 1),
    [],
);

export const cameraSetStart = defineCommand(
    'cameraSetStart',
    'Camera: Set Start',
    (cc, frame: number[]) => {
        const camera = cc.call(Camera);
        const objects = cc.call(ObjectManager);

        const rect = frame.length === 4
            ? Rect.fromArray(frame)
            : camera.getModelFrame();

        objects.update(u => {
            const prev = objects.getAll(CameraStartingPosition);
            u.deleteAll(Array.from(prev));
            const next = u.create(CameraStartingPosition);
            next.x = rect.left;
            next.y = rect.top;
            next.width = rect.width;
            next.height = rect.height;
        });

        cc.call(CommandDispatcher)(alert, ['Starting position for board has been set to your current view.']);
    },
    [pointStep('position', 'Position:')]

);

export const cameraZoomBy = defineCommand(
    'cameraZoomBy',
    'Camera: Zoom By',
    (cc, [z, at]: [number, [number, number]?]) => {
        const camera = cc.call(Camera);

        // z = the zoom delta
        // at = the view point we are zooming at

        // Scale the delta to keep the apparent zoom speed consistent
        z = z * camera.scale;

        doZoomHandlingAt(camera, () => camera.zoom(1 * z), at);
    },
);

export const cameraZoomStep = defineCommand(
    'cameraZoomStep',
    null,
    (cc, [ioo, at]: [number, [number, number]?]) => {
        const camera = cc.call(Camera);

        const rs = Math.round(camera.scale * 1000) / 1000;
        const steps = [
            0.05, 0.06, 0.07, 0.08, 0.09, 0.10,
            0.11, 0.121, 0.133, 0.146, 0.161, 0.177, 0.195, 0.214, 0.236,
            0.25, 0.285, 0.314, 0.345, 0.38, 0.418, 0.459, 0.50, 0.556,
            0.612, 0.673, 0.75, 0.814, 0.895, 1.00, 1.192, 1.311, 1.442,
            1.50, 1.755, 1.919, 2.111, 2.323, 2.50, 2.81, 3.00, 3.4,
            3.75, 4.0
        ];

        if (steps.includes(rs)) {
            const next = steps[steps.indexOf(rs) + ioo] ?? rs;
            doZoomHandlingAt(camera, () => camera.zoomTo(next), at);
        }
        else {
            const nearest = (ioo > 0)
                ? steps.filter(x => x > rs)[0]
                : steps.reverse().filter(x => x < rs)[0];
            doZoomHandlingAt(camera, () => camera.zoomTo(nearest), at);
        }
    },
);

export const cameraZoomTo = defineCommand(
    'cameraZoomTo',
    'Camera: Zoom To',
    (cc, [scale, at]: [number, [number, number]?]) => {
        const camera = cc.call(Camera);

        // scale = the scale to set
        // at = the view point we are zooming at

        doZoomHandlingAt(camera, () => camera.zoomTo(scale), at);
    },
    [
        numberStep('scale', 'Zoom (4 to 0.1):'),
    ]
);

export const cameraZoomReset = defineCommand(
    'cameraZoomReset',
    'Camera: Zoom Reset',
    cc => cc.call(CommandDispatcher)(cameraZoomTo, [1]),
);

export const cameraFrameContent = defineCommand(
    'cameraFrameContent',
    'Camera: Frame Content',
    async (cc, [padding]: [number?]) => {

        const camera = cc.call(Camera);
        const objects = cc.call(ObjectManager);

        const elmts = objects.getRoot().polarize(Physical);
        if (!elmts) return;

        const frame = Rect.fromMany(elmts.map(x => x.bounds()))
            .inflate(Number(padding) || 0);

        await camera.animateToFrame(frame);
    },
    [
        numberStep('padding', 'Padding (optional):')
    ]
);

export const cameraFrameSelection = defineCommand(
    'cameraFrameSelection',
    'Camera: Frame Selection',
    async (cc, [padding]: [number?]) => {

        const camera = cc.call(Camera);
        const selection = cc.call(EditorSession).get(Selection);

        const elmts = Physicals.top(selection);
        if (!elmts) return;

        const frame = Rect.fromMany(elmts.map(x => x.bounds(RelativeTo.Model)))
            .inflate(Number(padding) || 0);

        await camera.animateToFrame(frame);
    },
    [
        numberStep('padding', 'Padding (optional):')
    ]
);

export const cameraFrameName = defineCommand(
    'cameraFrameName',
    'Camera: Frame Name...',
    async (cc, [name]: [string]) => {
        if (!name) return;
        name = name.startsWith('@') ? name.slice(1) : name;

        const camera = cc.call(Camera);
        const objects = cc.call(ObjectManager);

        const hits = Phys.from(objects.findAll(Physical, x => x.name === name)).takeHeads();
        if (!hits.size) return;

        await camera.animateToFrame(hits.bounds());
    },
    [
        stringStep('name', 'Element Name:')
    ]
);

export const cameraLinkToPosition = defineCommand(
    'cameraLinkToPosition',
    'Camera: Link To Position',
    r => {

        const camera = r.call(Camera);
        const dispatch = r.call(CommandDispatcher);
        const clipboard = r.call(ClipboardService);

        const expr = camera.getModelFrame().toArray().map(x => Math.round(x)).join(',');
        const curr = location.href;
        const url = `${curr}${curr.includes('?') ? '&' : '?'}camera=${expr}`;

        const data = new ClipDataBuilder();
        data.setText(url);
        clipboard.write(data.build());

        dispatch(alert, ['Copied to clipboard!']);
    },
    [
        stringStep('Enter tag:')
    ]
);

const doZoomHandlingAt = (camera: Camera, zoom: VoidCallback, at?: [number, number]): void => {
    // If we are zooming "at" a point, get the camera position before zoom and after the zoom move the camera
    // using move delta of before and after factoring the scale.
    if (at) {
        const pt = Point.from(at);
        const before = camera.toModelPoint(pt);
        zoom();
        const after = camera.toModelPoint(pt);
        camera.move(before.subtract(after).multiply(camera.scale));
    }
    else {
        zoom();
    }
};
